import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    ArrowLeft,
    ArrowRight,
    CalendarDays,
    MapPin,
    Clock3,
    ShieldCheck,
    CreditCard,
    User as UserIcon,
    Lock,
    Mail,
    Phone,
    IdCard,
    BadgeCheck,
} from "lucide-react";
import { api, formatApiError, useAuth, setStoredToken } from "@/lib/api";

function formatDate(dateIso) {
    if (!dateIso) return "";
    return new Date(dateIso).toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
}

function PwdRules({ value }) {
    const rules = [
        { ok: value.length >= 8, label: "8+ caractères" },
        { ok: /[A-Z]/.test(value), label: "1 majuscule" },
        { ok: /\d/.test(value), label: "1 chiffre" },
        { ok: /[^A-Za-z0-9]/.test(value), label: "1 spécial" },
    ];
    return (
        <ul className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
            {rules.map((r) => (
                <li key={r.label} className={r.ok ? "text-green-700" : "text-brand-smoke"}>
                    {r.ok ? "✓" : "○"} {r.label}
                </li>
            ))}
        </ul>
    );
}

// IMPORTANT: Field MUST stay at module scope (see Signup.jsx comment).
// Declaring it inside Inscription() would re-create the component identity on
// every keystroke and cause React to unmount/remount inputs, losing focus.
function Field({ label, icon: Icon, ...rest }) {
    return (
        <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-brand-ink/80">{label}</label>
            <div className="relative mt-2">
                {Icon && <Icon className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />}
                <input
                    className={`w-full rounded-xl border border-brand-line bg-white py-3 ${Icon ? "pl-11" : "pl-4"} pr-4 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20`}
                    {...rest}
                />
            </div>
        </div>
    );
}

export default function Inscription() {
    const { sessionId } = useParams();
    const { user, setUser } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();

    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [mode, setMode] = useState("recap"); // "recap" | "signup"
    const [form, setForm] = useState({
        email: "",
        password: "",
        confirm: "",
        first_name: "",
        last_name: "",
        phone: "",
        adeli_rpps: "",
        accept_cgu: false,
    });

    useEffect(() => {
        if (params.get("canceled") === "1") {
            toast.error("Le paiement a été annulé. Vous pouvez réessayer.");
        }
    }, [params]);

    useEffect(() => {
        api.get(`/sessions/${sessionId}`)
            .then((r) => setSession(r.data))
            .catch(() => setSession(null))
            .finally(() => setLoading(false));
    }, [sessionId]);

    // Determine default mode based on auth state
    useEffect(() => {
        if (user) setMode("recap");
    }, [user]);

    const submit = async (e) => {
        e.preventDefault();
        if (!user) {
            // Inline signup validation
            if (!form.accept_cgu) return toast.error("Vous devez accepter les CGU.");
            if (form.password !== form.confirm)
                return toast.error("Les mots de passe ne correspondent pas.");
            const rulesOk =
                form.password.length >= 8 &&
                /[A-Z]/.test(form.password) &&
                /\d/.test(form.password) &&
                /[^A-Za-z0-9]/.test(form.password);
            if (!rulesOk) return toast.error("Mot de passe trop faible.");
        }
        setSubmitting(true);
        try {
            const body = {
                session_id: sessionId,
                origin_url: window.location.origin,
            };
            if (!user) {
                body.anonymous_signup = {
                    email: form.email.trim().toLowerCase(),
                    password: form.password,
                    first_name: form.first_name.trim(),
                    last_name: form.last_name.trim(),
                    phone: form.phone.trim() || null,
                    adeli_rpps: form.adeli_rpps.trim() || null,
                    accept_cgu: form.accept_cgu,
                };
            }
            const { data } = await api.post("/payments/checkout", body);
            if (data.access_token) {
                setStoredToken(data.access_token);
                setUser(data.user);
            }
            if (data.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                toast.error("Impossible d'obtenir l'URL de paiement.");
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center text-brand-smoke" data-testid="inscription-loading">
                Chargement…
            </div>
        );
    }

    if (!session) {
        return (
            <section className="py-32 text-center" data-testid="inscription-session-not-found">
                <p className="text-brand-ink/70">
                    Session introuvable — retournez à la{" "}
                    <Link to="/sessions" className="font-semibold text-brand-orange">
                        liste des sessions
                    </Link>
                    .
                </p>
            </section>
        );
    }

    return (
        <div data-testid="page-inscription" className="bg-[#FAFAFA]">
            {/* Hero */}
            <section className="relative bg-brand-ink text-white pt-32 pb-14 md:pt-40 grain">
                <div className="mx-auto max-w-5xl px-6">
                    <Link to="/sessions" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-white/60 hover:text-white">
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Retour aux sessions
                    </Link>
                    <div className="mt-8 text-xs uppercase tracking-[0.25em] text-brand-orange">Inscription sécurisée</div>
                    <h1 className="mt-4 font-display text-4xl leading-[1.05] tracking-tightest sm:text-5xl lg:text-6xl">
                        {session.formation_name}
                    </h1>
                    <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/80">
                        <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4 text-brand-orange" /> {formatDate(session.start_date)}</span>
                        <span className="inline-flex items-center gap-2"><Clock3 className="h-4 w-4 text-brand-orange" /> {session.start_time}–{session.end_time}</span>
                        <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-orange" /> {session.venue_name} · {session.city}</span>
                    </div>
                </div>
            </section>

            {/* Body */}
            <section className="py-14 md:py-20">
                <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-6 lg:grid-cols-[1fr_420px]">
                    {/* Left — signup / recap */}
                    <motion.div
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="rounded-3xl border border-brand-line bg-white p-8 shadow-premium md:p-10"
                    >
                        {user ? (
                            <div data-testid="inscription-auth-recap">
                                <div className="flex items-center gap-3 text-sm text-brand-ink">
                                    <BadgeCheck className="h-5 w-5 text-green-600" />
                                    Vous êtes connecté·e en tant que <strong>{user.first_name} {user.last_name}</strong> ({user.email}).
                                </div>
                                {!user.adeli_rpps && (
                                    <div className="mt-6 rounded-xl border border-brand-orange/40 bg-brand-orange/5 p-4 text-sm text-brand-ink/85">
                                        <strong>Astuce :</strong> pensez à renseigner votre numéro <strong>ADELI/RPPS</strong> dans votre{" "}
                                        <Link to="/stagiaire/profil" className="font-semibold text-brand-orange hover:underline">profil</Link> — il apparaîtra automatiquement sur votre convention et votre attestation (démarches FIF PL / DPC).
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                <h2 className="font-display text-3xl">Créer votre compte</h2>
                                <p className="mt-2 text-sm text-brand-smoke">
                                    Déjà inscrit·e ?{" "}
                                    <Link to="/login" state={{ from: `/inscription/${sessionId}` }} className="font-semibold text-brand-orange hover:underline" data-testid="inscription-login-link">
                                        Se connecter
                                    </Link>
                                </p>
                                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="inscription-signup-form">
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Field label="Prénom *" icon={UserIcon} required value={form.first_name} onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))} data-testid="inscription-first-name" />
                                        <Field label="Nom *" icon={UserIcon} required value={form.last_name} onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))} data-testid="inscription-last-name" />
                                    </div>
                                    <Field label="Email *" icon={Mail} type="email" required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="inscription-email" />
                                    <Field label="Téléphone" icon={Phone} value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="inscription-phone" />
                                    <div>
                                        <Field label="Numéro ADELI / RPPS (optionnel mais recommandé)" icon={IdCard} value={form.adeli_rpps} onChange={(e) => setForm((f) => ({ ...f, adeli_rpps: e.target.value }))} data-testid="inscription-adeli" placeholder="11 chiffres" />
                                        <div className="mt-1.5 text-[11px] text-brand-smoke">
                                            Utilisé pour pré-remplir vos documents FIF PL et DPC. Vous pourrez le renseigner plus tard.
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold uppercase tracking-wider text-brand-ink/80">Mot de passe *</label>
                                        <div className="relative mt-2">
                                            <Lock className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                                            <input
                                                type="password"
                                                required
                                                value={form.password}
                                                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                                                data-testid="inscription-password"
                                                className="w-full rounded-xl border border-brand-line bg-white py-3 pl-11 pr-4 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
                                            />
                                        </div>
                                        <PwdRules value={form.password} />
                                    </div>
                                    <Field label="Confirmer mot de passe *" icon={Lock} type="password" required value={form.confirm} onChange={(e) => setForm((f) => ({ ...f, confirm: e.target.value }))} data-testid="inscription-confirm" />
                                    <label className="flex items-start gap-3 rounded-xl border border-brand-line bg-[#FAFAFA] p-4 text-sm">
                                        <input type="checkbox" required checked={form.accept_cgu} onChange={(e) => setForm((f) => ({ ...f, accept_cgu: e.target.checked }))} data-testid="inscription-cgu" className="mt-0.5 h-4 w-4 accent-brand-orange" />
                                        <span className="text-brand-ink/85">
                                            J'accepte les <Link to="/cgu" className="text-brand-orange hover:underline">Conditions Générales d'Utilisation</Link> et confirme mon inscription.
                                        </span>
                                    </label>
                                </form>
                            </>
                        )}
                    </motion.div>

                    {/* Right — Summary + Pay button */}
                    <motion.aside
                        initial={{ opacity: 0, y: 14 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.55, delay: 0.1 }}
                        className="h-max lg:sticky lg:top-28"
                    >
                        <div className="rounded-3xl border border-brand-line bg-white p-8 shadow-premium">
                            <div className="text-xs uppercase tracking-widest text-brand-orange">Récapitulatif</div>
                            <h3 className="mt-3 font-display text-2xl leading-tight">{session.formation_name}</h3>
                            <dl className="mt-6 space-y-3 text-sm">
                                <div className="flex items-start gap-3"><CalendarDays className="mt-0.5 h-4 w-4 text-brand-orange" /><span>{formatDate(session.start_date)} · {session.start_time}–{session.end_time}</span></div>
                                <div className="flex items-start gap-3"><MapPin className="mt-0.5 h-4 w-4 text-brand-orange" /><span>{session.venue_name}{session.venue_address ? `, ${session.venue_address}` : ""} — {session.city}</span></div>
                                <div className="flex items-start gap-3"><UserIcon className="mt-0.5 h-4 w-4 text-brand-orange" /><span>{session.trainer_name || "Formateur à confirmer"}</span></div>
                            </dl>
                            <div className="mt-8 border-t border-brand-line pt-6">
                                <div className="flex items-baseline justify-between">
                                    <span className="text-xs uppercase tracking-wider text-brand-smoke">Montant TTC</span>
                                    <span className="font-display text-4xl text-brand-ink">390 €</span>
                                </div>
                                <div className="mt-2 text-xs text-brand-smoke">
                                    <strong className="text-brand-orange">Prise en charge possible</strong> via FIF PL &amp; DPC —{" "}
                                    <Link to="/prise-en-charge" className="underline">voir les modalités</Link>.
                                </div>
                            </div>
                            <button
                                type="submit"
                                onClick={submit}
                                disabled={submitting}
                                data-testid="inscription-pay-btn"
                                className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-orange px-6 py-4 text-sm font-semibold text-white transition-all hover:bg-[#d9772c] disabled:opacity-60"
                            >
                                <CreditCard className="h-4 w-4" />
                                {submitting ? "Redirection…" : "Payer 390 € et confirmer"}
                                <ArrowRight className="h-4 w-4" />
                            </button>
                            <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-brand-smoke">
                                <ShieldCheck className="h-3.5 w-3.5 text-green-600" />
                                Paiement sécurisé par Stripe · données chiffrées
                            </div>
                        </div>
                    </motion.aside>
                </div>
            </section>
        </div>
    );
}
